import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const CONSENT_KEY = 'uk_petition_cookie_consent';
const GA_ID = 'G-H4GRQ01GG0';

// ── Public helpers ────────────────────────────────────────────────────────────

export const loadGA4 = () => {
    if (window.gaLoaded) return;
    window.gaLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID);
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
};

export const getConsent = () => localStorage.getItem(CONSENT_KEY);

export const resetConsent = () => {
    localStorage.removeItem(CONSENT_KEY);
    window.dispatchEvent(new Event('cookie_consent_reset'));
};

// ── Styles ────────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const scaleIn = keyframes`
    from { opacity: 0; transform: scale(0.92); }
    to   { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeIn} 0.25s ease;
`;

const Modal = styled.div`
    background: #ffffff;
    border-radius: 16px;
    max-width: 480px;
    width: 100%;
    padding: 36px 32px 28px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
    animation: ${scaleIn} 0.25s ease;
    text-align: center;
`;

const IconWrap = styled.div`
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    font-size: 24px;
`;

const ModalTitle = styled.h2`
    font-size: 1.25em;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px;
`;

const ModalText = styled.p`
    font-size: 0.9em;
    color: #666;
    line-height: 1.6;
    margin: 0 0 8px;

    a {
        color: #667eea;
        text-decoration: none;
        &:hover { text-decoration: underline; }
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #f0f0f0;
    margin: 20px 0;
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const AcceptBtn = styled.button`
    width: 100%;
    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
    color: white;
    border: none;
    border-radius: 10px;
    padding: 13px 20px;
    font-size: 0.95em;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.88; }
`;

const DeclineBtn = styled.button`
    width: 100%;
    background: transparent;
    color: #888;
    border: 1.5px solid #e0e0e0;
    border-radius: 10px;
    padding: 11px 20px;
    font-size: 0.9em;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;

    &:hover {
        color: #444;
        border-color: #bbb;
    }
`;

const Note = styled.p`
    font-size: 0.78em;
    color: #aaa;
    margin: 14px 0 0;
    line-height: 1.5;

    a {
        color: #999;
        text-decoration: underline;
    }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const CookieConsent = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const consent = getConsent();
        if (!consent) {
            const t = setTimeout(() => setVisible(true), 600);
            return () => clearTimeout(t);
        }
        if (consent === 'accepted') loadGA4();
    }, []);

    useEffect(() => {
        const handleReset = () => setVisible(true);
        window.addEventListener('cookie_consent_reset', handleReset);
        return () => window.removeEventListener('cookie_consent_reset', handleReset);
    }, []);

    // Prevent background scroll while modal is open
    useEffect(() => {
        if (visible) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [visible]);

    const handleAccept = () => {
        localStorage.setItem(CONSENT_KEY, 'accepted');
        loadGA4();
        setVisible(false);
    };

    const handleDecline = () => {
        localStorage.setItem(CONSENT_KEY, 'declined');
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <Overlay>
            <Modal role="dialog" aria-modal="true" aria-labelledby="cookie-title">
                <IconWrap>&#127850;</IconWrap>

                <ModalTitle id="cookie-title">We value your privacy</ModalTitle>

                <ModalText>
                    We use analytics cookies to understand how people use this site and
                    improve it over time. Only anonymous usage statistics are collected —
                    no names, emails, or any identifiable information.
                </ModalText>

                <ModalText>
                    Either way, the site works fully.
                </ModalText>

                <Divider />

                <Buttons>
                    <AcceptBtn onClick={handleAccept}>Accept</AcceptBtn>
                    <DeclineBtn onClick={handleDecline}>Reject</DeclineBtn>
                </Buttons>

                <Note>
                    By accepting you consent to Google Analytics 4 tracking your usage.
                    Change your choice anytime via "Cookie Preferences" in the footer.{' '}
                    <a href="/privacy">Privacy Policy</a>
                </Note>
            </Modal>
        </Overlay>
    );
};

export default CookieConsent;
